import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Alert,
  Snackbar,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import Select from '../components/SafeSelect';
import { useAdmin } from '../context/AdminContext';
import { isValidEmail, isValidPhone, generateDriverId, VEHICLE_TYPES, VEHICLE_MODELS } from '../utils/helpers';
import DriverCard from '../components/DriverCard';
import DriverDetailsModal from '../components/DriverDetailsModal';

const Drivers = () => {
  const {
    drivers,
    vehicles,
    approveDriver,
    rejectDriver,
    createDriver,
    updateDriver,
    deleteDriver,
    lockDriver,
    unlockDriver,
  } = useAdmin();
  const [openDialog, setOpenDialog] = useState(false);
  const [openVehicleDialog, setOpenVehicleDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedDriverForDetails, setSelectedDriverForDetails] = useState(null);
  const [openDriverDetailsModal, setOpenDriverDetailsModal] = useState(false);
  const [selectedDriverForVehicle, setSelectedDriverForVehicle] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [filter, setFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formData, setFormData] = useState({
    driverId: '',
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    vehicleType: '',
    licenseNumber: '',
    availableUntil: '',
    ownVehicle: {
      type: '',
      model: '',
      year: '',
      plateNumber: '',
      seats: ''
    }
  });

  const handleOpenDialog = (driver = null) => {
    if (driver) {
      setEditMode(true);
      setSelectedDriver(driver);
      setFormData({
        driverId: driver.driverId || '',
        name: driver.name || '',
        email: driver.email || '',
        phone: driver.phone || '',
        password: '',
        confirmPassword: '',
        vehicleType: driver.vehicleType || '',
        licenseNumber: driver.licenseNumber || '',
        availableUntil: driver.availableUntil ? driver.availableUntil.toDate().toISOString().slice(0, 16) : '',
          ownVehicle: driver.ownVehicle || {
            type: '',
            model: '',
            year: '',
            plateNumber: '',
            seats: ''
          }
      });
    } else {
      setEditMode(false);
      setSelectedDriver(null);
      setFormData({
        driverId: generateDriverId(),
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        vehicleType: '',
        licenseNumber: '',
        availableUntil: '',
        ownVehicle: {
          type: '',
          model: '',
          year: '',
          plateNumber: '',
          seats: ''
        }
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedDriver(null);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleOwnVehicleChange = (e) => {
    setFormData({
      ...formData,
      ownVehicle: {
        ...formData.ownVehicle,
        [e.target.name]: e.target.value
      }
    });
  };

  // Vehicle Assignment Handlers
  const handleOpenVehicleAssignment = (driver) => {
    setSelectedDriverForVehicle(driver);
    setSelectedVehicleId(driver.assignedVehicle?.id || '');
    setOpenVehicleDialog(true);
  };

  const handleCloseVehicleDialog = () => {
    setOpenVehicleDialog(false);
    setSelectedDriverForVehicle(null);
    setSelectedVehicleId('');
  };

  const handleAssignVehicle = async () => {
    if (!selectedDriverForVehicle || !selectedVehicleId) {
      showSnackbar('Please select a vehicle', 'error');
      return;
    }

    const selectedVehicle = vehicles.find(v => v.id === selectedVehicleId);
    if (!selectedVehicle) {
      showSnackbar('Vehicle not found', 'error');
      return;
    }

    // Update driver with assigned vehicle
    const result = await updateDriver(selectedDriverForVehicle.id, {
      assignedVehicle: {
        id: selectedVehicle.id,
        type: selectedVehicle.vehicleType || selectedVehicle.type,
        model: selectedVehicle.model,
        plateNumber: selectedVehicle.licensePlate || selectedVehicle.plateNumber,
        seats: selectedVehicle.seats || selectedVehicle.capacity
      }
    });

    if (result.success) {
      showSnackbar('Vehicle assigned successfully', 'success');
      handleCloseVehicleDialog();
    } else {
      showSnackbar(result.error || 'Failed to assign vehicle', 'error');
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      showSnackbar('Name is required', 'error');
      return false;
    }
    if (!isValidEmail(formData.email)) {
      showSnackbar('Valid email is required', 'error');
      return false;
    }
    if (!isValidPhone(formData.phone)) {
      showSnackbar('Valid phone number is required', 'error');
      return false;
    }
    // Password validation for new drivers
    if (!editMode) {
      if (!formData.password) {
        showSnackbar('Password is required for new drivers', 'error');
        return false;
      }
      if (formData.password.length < 6) {
        showSnackbar('Password must be at least 6 characters', 'error');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        showSnackbar('Passwords do not match', 'error');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const driverData = {
      ...formData,
      availableUntil: formData.availableUntil ? new Date(formData.availableUntil) : null
    };

    let result;
    if (editMode) {
      // Remove password fields when editing
      const { password, confirmPassword, ...updateData } = driverData;
      result = await updateDriver(selectedDriver.id, updateData);
    } else {
      result = await createDriver(driverData);
    }

    if (result.success) {
      const message = result.message || `Driver ${editMode ? 'updated' : 'created'} successfully`;
      showSnackbar(message, 'success');
      if (result.warning) {
        setTimeout(() => showSnackbar(result.warning, 'warning'), 2000);
      }
      handleCloseDialog();
    } else {
      showSnackbar(result.error || 'Operation failed', 'error');
    }
  };

  const handleApprove = async (driverId) => {
    const result = await approveDriver(driverId);
    if (result.success) {
      showSnackbar('Driver approved successfully', 'success');
    } else {
      showSnackbar(result.error || 'Failed to approve driver', 'error');
    }
  };

  const handleReject = async (driverId) => {
    const reason = prompt('Enter rejection reason (optional):');
    const result = await rejectDriver(driverId, reason);
    if (result.success) {
      showSnackbar('Driver rejected', 'warning');
    } else {
      showSnackbar(result.error || 'Failed to reject driver', 'error');
    }
  };

  const handleDelete = async (driverId) => {
    if (window.confirm('Are you sure you want to delete this driver?')) {
      const result = await deleteDriver(driverId);
      if (result.success) {
        showSnackbar('Driver deleted successfully', 'success');
      } else {
        showSnackbar(result.error || 'Failed to delete driver', 'error');
      }
    }
  };

  const handleToggleLock = async (driver) => {
    if (!driver) {
      return;
    }

    if (driver.isLocked) {
      const shouldUnlock = window.confirm(
        `Unlock access for ${driver.name || 'this driver'}?`
      );
      if (!shouldUnlock) {
        return;
      }

      const result = await unlockDriver(driver.id);
      if (result.success) {
        showSnackbar('Driver access unlocked successfully', 'success');
      } else {
        showSnackbar(result.error || 'Failed to unlock driver access', 'error');
      }
      return;
    }

    const reasonInput = window.prompt(
      'Enter lock reason (e.g., license expired, documents pending):',
      'Access locked by admin',
    );

    if (reasonInput === null) {
      return;
    }

    const lockDocument = window.prompt(
      'If due to document expiry, enter document name (optional):',
      '',
    );

    const normalizedReason = String(reasonInput || '').trim();
    const normalizedDocument = String(lockDocument || '').trim();

    const result = await lockDriver(driver.id, normalizedReason, {
      lockDocument: normalizedDocument,
      lockCategory: normalizedDocument ? 'document_expiry' : 'manual_lock',
    });

    if (result.success) {
      showSnackbar('Driver access locked successfully', 'warning');
    } else {
      showSnackbar(result.error || 'Failed to lock driver access', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDriverDetails = (driver) => {
    setSelectedDriverForDetails(driver);
    setOpenDriverDetailsModal(true);
  };

  const handleCloseDriverDetails = () => {
    setOpenDriverDetailsModal(false);
    setSelectedDriverForDetails(null);
  };

  // Filter drivers
  const filteredDrivers = drivers.filter(driver => {
    if (filter === 'all') return true;
    if (filter === 'pending') return !driver.approved && driver.status !== 'rejected';
    if (filter === 'approved') return driver.approved;
    if (filter === 'rejected') return driver.status === 'rejected';
    if (filter === 'locked') return Boolean(driver.isLocked);
    return true;
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Driver Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Driver
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FilterIcon color="action" />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={filter}
              label="Status Filter"
              onChange={(e) => setFilter(e.target.value)}
            >
              <MenuItem value="all">All Drivers</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="locked">Locked</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredDrivers.length} of {drivers.length} drivers
          </Typography>
        </Box>
      </Paper>

      {/* Drivers Card Grid */}
      <Box sx={{ mb: 4 }}>
        {filteredDrivers.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No drivers found
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {filteredDrivers.map((driver) => (
              <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={driver.id}>
                <DriverCard
                  driver={driver}
                  onClick={handleOpenDriverDetails}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onEdit={handleOpenDialog}
                  onDelete={handleDelete}
                  onAssignVehicle={handleOpenVehicleAssignment}
                  onToggleLock={handleToggleLock}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Add/Edit Driver Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Driver' : 'Add New Driver'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Driver ID"
              name="driverId"
              value={formData.driverId}
              onChange={handleInputChange}
              fullWidth
              disabled={editMode}
            />
            <TextField
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Phone Number"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              fullWidth
              required
            />
            {!editMode && (
              <>
                <TextField
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  helperText="Password for driver login (minimum 6 characters)"
                />
                <TextField
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  helperText="Re-enter password to confirm"
                  error={formData.confirmPassword && formData.password !== formData.confirmPassword}
                />
              </>
            )}
            <FormControl fullWidth>
              <InputLabel>Vehicle Type</InputLabel>
              <Select
                name="vehicleType"
                value={formData.vehicleType}
                label="Vehicle Type"
                onChange={handleInputChange}
              >
                <MenuItem value="Car">Car</MenuItem>
                <MenuItem value="Van">Van</MenuItem>
                <MenuItem value="Truck">Truck</MenuItem>
                <MenuItem value="Motorcycle">Motorcycle</MenuItem>
                <MenuItem value="Any">Any</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="License Number"
              name="licenseNumber"
              value={formData.licenseNumber}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField
              label="Available Until"
              name="availableUntil"
              type="datetime-local"
              value={formData.availableUntil}
              onChange={handleInputChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            {/* Own Vehicle Section */}
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Driver's Own Vehicle (Optional)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Vehicle Type</InputLabel>
                  <Select
                    name="type"
                    value={formData.ownVehicle.type}
                    label="Vehicle Type"
                    onChange={handleOwnVehicleChange}
                  >
                    <MenuItem value="">None</MenuItem>
                    {VEHICLE_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Model</InputLabel>
                  <Select
                    name="model"
                    value={formData.ownVehicle.model}
                    label="Model"
                    onChange={handleOwnVehicleChange}
                  >
                    <MenuItem value="">None</MenuItem>
                    {VEHICLE_MODELS.map((m) => (
                      <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Year"
                  name="year"
                  value={formData.ownVehicle.year}
                  onChange={handleOwnVehicleChange}
                  fullWidth
                  size="small"
                  placeholder="e.g., 2020"
                />
                <TextField
                  label="Plate Number"
                  name="plateNumber"
                  value={formData.ownVehicle.plateNumber}
                  onChange={handleOwnVehicleChange}
                  fullWidth
                  size="small"
                  placeholder="e.g., ABC-123"
                />
                <TextField
                  label="Number of Seats"
                  name="seats"
                  type="number"
                  value={formData.ownVehicle.seats}
                  onChange={handleOwnVehicleChange}
                  fullWidth
                  size="small"
                  placeholder="e.g., 5, 7"
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vehicle Assignment Dialog */}
      <Dialog open={openVehicleDialog} onClose={handleCloseVehicleDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign Vehicle to {selectedDriverForVehicle?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Show driver's own vehicle info */}
            {selectedDriverForVehicle?.ownVehicle?.type && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Driver has their own vehicle:
                </Typography>
                <Typography variant="body2">
                  {selectedDriverForVehicle.ownVehicle.type} - {selectedDriverForVehicle.ownVehicle.model}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedDriverForVehicle.ownVehicle.seats} seats | {selectedDriverForVehicle.ownVehicle.plateNumber}
                </Typography>
              </Alert>
            )}

            <Typography variant="body2" sx={{ mb: 2 }}>
              Select a vehicle from Garage to assign to this driver. The driver will park their personal vehicle (if any) at the Garage and use the assigned company vehicle for bookings.
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Select Vehicle</InputLabel>
              <Select
                value={selectedVehicleId}
                label="Select Vehicle"
                onChange={(e) => setSelectedVehicleId(e.target.value)}
              >
                <MenuItem value="">
                  <em>None (Use own vehicle)</em>
                </MenuItem>
                {vehicles.filter(v => v.status === 'available').map((vehicle) => (
                  <MenuItem key={vehicle.id} value={vehicle.id}>
                    {vehicle.type} - {vehicle.make} {vehicle.model} ({vehicle.year}) - {vehicle.plateNumber} - {vehicle.seats} seats
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedVehicleId && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  ✓ Driver will use this company vehicle for bookings
                </Typography>
                {selectedDriverForVehicle?.ownVehicle?.type && (
                  <Typography variant="caption" color="text.secondary">
                    Their personal vehicle will be parked at Garage
                  </Typography>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVehicleDialog}>Cancel</Button>
          <Button 
            onClick={handleAssignVehicle} 
            variant="contained"
            disabled={!selectedVehicleId}
          >
            Assign Vehicle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Driver Details Modal */}
      <DriverDetailsModal
        open={openDriverDetailsModal}
        onClose={handleCloseDriverDetails}
        driver={selectedDriverForDetails}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Drivers;
